import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { Box, Typography, Button, Paper, Alert, CircularProgress, Stack, Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { supabase } from '../../../supabaseClient.js';
import useAlert from '../../../alert/useAlert.js';
import PackageEditor from './PackageEditor.js';
import { DEFAULT_PACKAGE_SEEDS, isPackageDraftValid, parsePriceToCents, formatHKD, computePackagePriceCents } from './packagePricing.js';

const buttonStyle = { textTransform: 'none', borderRadius: '8px', boxShadow: 'none' };
const newDraft = (seed = { name: '', sessions: 4, discount_pct: 0 }) => ({
  ...seed, id: uuid(), _new: true, active: true, addons: [],
});

export default function SessionPackages({ onEditProfile, embedded = false, profileRate, savedProfileRate, profileSaving = false, onDirtyChange }) {
  const userId = useSelector((state) => state.globalState.userDetails?.id);
  const { setAlert } = useAlert();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [doctor, setDoctor] = useState(null);
  const [basePriceCents, setBasePriceCents] = useState(null);
  const [packages, setPackages] = useState([]);
  const [savedPackages, setSavedPackages] = useState([]);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const loadGeneration = useRef(0);

  const load = useCallback(async () => {
    const generation = ++loadGeneration.current;
    setLoading(true);
    setLoadFailed(false);
    try {
      if (!userId) throw new Error('Sign in to manage your packages.');
      const { data: doctors, error: doctorError } = await supabase.from('doctors')
        .select('id, session_price').eq('user_id', userId).order('display_order', { ascending: true }).limit(1);
      if (doctorError) throw doctorError;
      const resolvedDoctor = doctors?.[0] || null;
      let cents = null;
      let drafts = [];
      if (resolvedDoctor) {
        const { data: account, error: accountError } = await supabase.from('user_accounts')
          .select('regular_session_price, first_session_price').eq('id', userId).maybeSingle();
        if (accountError) throw accountError;
        let price = account?.regular_session_price || account?.first_session_price;
        if (typeof price === 'string' && price.startsWith('\\x')) {
          const { data, error } = await supabase.rpc('decrypt_text', { encrypted_data: price });
          if (error) throw error;
          price = data;
        }
        cents = parsePriceToCents(price) || parsePriceToCents(resolvedDoctor.session_price, 'cents');
        const { data: rows, error } = await supabase.from('therapist_packages')
          .select('id, name, sessions, discount_pct, active, updated_at')
          .eq('doctor_id', resolvedDoctor.id).is('superseded_by', null).order('created_at', { ascending: true });
        if (error) throw error;
        let addons = [];
        if (rows?.length) {
          const result = await supabase.from('therapist_package_addons')
            .select('package_id, title, description, sort_order').in('package_id', rows.map((row) => row.id))
            .order('sort_order', { ascending: true });
          if (result.error) throw result.error;
          addons = result.data || [];
        }
        drafts = (rows || []).map((row) => ({ ...row, addons: addons.filter((addon) => addon.package_id === row.id)
          .map(({ title, description }) => ({ title, description: description || '' })) }));
      }
      if (generation !== loadGeneration.current) return;
      setDoctor(resolvedDoctor);
      setBasePriceCents(cents);
      setPackages(drafts);
      setSavedPackages(drafts);
      setExpandedIndex(null);
    } catch (error) {
      if (generation === loadGeneration.current) setLoadFailed(true);
    } finally {
      if (generation === loadGeneration.current) setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
    return () => { loadGeneration.current += 1; };
  }, [load]);

  const updatePackage = (index, patch) => setPackages((prev) => prev.map((pkg, i) => i === index ? { ...pkg, ...patch } : pkg));
  const updateAddons = (index, change) => setPackages((prev) => prev.map((pkg, i) => i === index ? { ...pkg, addons: change(pkg.addons) } : pkg));
  const dirty = JSON.stringify(packages) !== JSON.stringify(savedPackages);
  const rateUnsaved = embedded && parsePriceToCents(profileRate) !== parsePriceToCents(savedProfileRate);
  const previewPriceCents = embedded ? parsePriceToCents(profileRate) : basePriceCents;
  const effectiveSavedPriceCents = embedded ? parsePriceToCents(savedProfileRate) : basePriceCents;
  useEffect(() => { onDirtyChange?.(dirty || saving); }, [dirty, saving, onDirtyChange]);
  const editingDisabled = !doctor || saving || loadFailed;

  const handleSave = async () => {
    if (editingDisabled || rateUnsaved || profileSaving || !dirty || !packages.length) return;
    const invalid = packages.findIndex((pkg) => !isPackageDraftValid(pkg));
    if (invalid >= 0) {
      setExpandedIndex(invalid);
      setAlert('Please fix the highlighted fields before saving.', 'error');
      return;
    }
    if (!effectiveSavedPriceCents && packages.some((pkg) => pkg.active)) {
      setAlert('Set your session price before publishing packages.', 'error');
      return;
    }
    setSaving(true);
    try {
      const { data, error } = await supabase.rpc('save_therapist_packages', {
        p_doctor_id: doctor.id,
        p_packages: packages.map((pkg) => ({ ...pkg, name: pkg.name.trim(), sessions: Number(pkg.sessions),
          discount_pct: Number(pkg.discount_pct), addons: pkg.addons.map((addon) => ({
            title: addon.title.trim(), description: addon.description?.trim() || null,
          })) })),
      });
      if (error) throw error;
      if (!Array.isArray(data) || data.length !== packages.length) throw new Error('Unable to confirm the save. Reload packages before retrying.');
      setPackages(data);
      setSavedPackages(data);
      setExpandedIndex(null);
      setAlert('Packages saved. Existing purchases keep their original package details.', 'success');
    } catch (error) {
      setAlert(error.message || 'Could not save packages. Your edits are still here.', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={28} /></Box>;

  return (
    <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: '12px', border: '1px solid #E5E7EB' }}>
      <Typography variant="h5" sx={{ fontWeight: 600, color: '#111827' }}>Session Packages</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Create session bundles and include extras for clients on your MindVault profile.
      </Typography>
      {embedded && rateUnsaved && <Alert severity="warning" sx={{ mt: 2 }}>Preview uses your unsaved regular session rate. Update your profile before saving packages.</Alert>}
      {loadFailed ? (
        <Alert severity="error" sx={{ mt: 3 }} action={<Button color="inherit" onClick={load}>Retry</Button>}>
          Could not load your packages and inclusions. Please retry before editing.
        </Alert>
      ) : !doctor ? (
        <Alert severity="info" sx={{ mt: 3 }} action={onEditProfile && <Button onClick={onEditProfile}>Set up profile</Button>}>
          Complete your MindVault therapist profile to offer packages.
        </Alert>
      ) : (
        <>
          <Box sx={{ mt: 3, p: 2, borderRadius: '8px', backgroundColor: previewPriceCents ? '#F0FAF9' : '#FFF8EC', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{previewPriceCents ? `${formatHKD(previewPriceCents)} per session` : 'Set your session price'}</Typography>
              <Typography variant="caption" color="text.secondary">{previewPriceCents ? 'Package prices use your regular session rate.' : 'You can draft packages now. Add a session price before publishing.'}</Typography>
            </Box>
            {onEditProfile && <Button onClick={onEditProfile} sx={buttonStyle}>{previewPriceCents ? 'Edit price' : 'Set session price'}</Button>}
          </Box>

          {packages.length === 0 ? (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Start with a package</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Use these defaults as a starting point, or create your own.</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5, mt: 2 }}>
                {DEFAULT_PACKAGE_SEEDS.map((seed) => (
                  <Box key={seed.name} sx={{ p: 2, border: '1px solid #E5E7EB', borderRadius: '10px' }}>
                    <Typography sx={{ fontWeight: 600 }}>{seed.name}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{seed.sessions} session{seed.sessions === 1 ? '' : 's'}</Typography>
                    <Chip size="small" label={seed.discount_pct ? `${seed.discount_pct}% off` : 'Standard rate'} sx={{ mt: 1.5, bgcolor: '#E5F6F4', color: '#167D79' }} />
                    {previewPriceCents && <Typography sx={{ mt: 1.5, fontWeight: 600 }}>{formatHKD(computePackagePriceCents(previewPriceCents, seed.sessions, seed.discount_pct))}</Typography>}
                  </Box>
                ))}
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2 }}>
                <Button variant="contained" sx={{ ...buttonStyle, bgcolor: '#29B8B3' }} onClick={() => { setPackages(DEFAULT_PACKAGE_SEEDS.map(newDraft)); setExpandedIndex(0); }}>Use default packages</Button>
                <Button variant="outlined" sx={buttonStyle} startIcon={<AddIcon />} onClick={() => { setPackages([newDraft()]); setExpandedIndex(0); }}>Create package</Button>
              </Stack>
            </Box>
          ) : (
            <Box sx={{ mt: 3 }}>
              {packages.map((draft, index) => (
                <PackageEditor key={draft.id} draft={draft} basePriceCents={previewPriceCents}
                  expanded={expandedIndex === index} disabled={editingDisabled}
                  onToggleExpanded={() => setExpandedIndex(expandedIndex === index ? null : index)}
                  onChange={(patch) => updatePackage(index, patch)}
                  onRemove={draft._new ? () => { setPackages((prev) => prev.filter((_, i) => i !== index)); setExpandedIndex(null); } : undefined}
                  onAddonChange={(j, patch) => updateAddons(index, (addons) => addons.map((addon, k) => k === j ? { ...addon, ...patch } : addon))}
                  onAddonAdd={() => updateAddons(index, (addons) => [...addons, { title: '', description: '' }])}
                  onAddonRemove={(j) => updateAddons(index, (addons) => addons.filter((_, k) => k !== j))}
                  onAddonMove={(j, direction) => updateAddons(index, (addons) => { const next = [...addons]; const target = j + direction; if (target >= 0 && target < next.length) [next[j], next[target]] = [next[target], next[j]]; return next; })}
                />
              ))}
              <Button startIcon={<AddIcon />} disabled={editingDisabled || packages.length >= 50} sx={buttonStyle}
                onClick={() => { setPackages((prev) => [...prev, newDraft()]); setExpandedIndex(packages.length); }}>Add package</Button>
            </Box>
          )}

          {packages.length > 0 && (
            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #E5E7EB' }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>{dirty ? 'You have unsaved changes' : 'All changes saved'}</Typography>
                <Button disabled={!dirty || saving} sx={buttonStyle} onClick={() => { setPackages(savedPackages); setExpandedIndex(null); }}>Discard changes</Button>
                <Button variant="contained" disabled={editingDisabled || rateUnsaved || profileSaving || !dirty || (!effectiveSavedPriceCents && packages.some((pkg) => pkg.active))} onClick={handleSave} sx={{ ...buttonStyle, bgcolor: '#29B8B3' }}>{saving ? 'Saving…' : 'Save packages'}</Button>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>Changes apply to new purchases. Existing clients keep the package they bought.</Typography>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
}
